import React from 'react';
import { UseFormWatch, Path, FieldValues } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';

// import { Box } from '../../index';

interface Condition<T extends FieldValues> {
  field: Path<T>;
  operator: 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan' | 'in' | 'notIn';
  value: unknown;
}

interface ConditionalFieldsProps<T extends FieldValues> {
  watch: UseFormWatch<T>;
  conditions: Condition<T> | Condition<T>[];
  mode?: 'all' | 'any';
  children: React.ReactNode;
  animationType?: 'fade' | 'slide' | 'scale' | 'none';
  onShow?: () => void;
  onHide?: () => void;
}

export function ConditionalFields<T extends FieldValues>({
  watch,
  conditions,
  mode = 'all',
  children,
  animationType = 'fade',
  onShow,
  onHide,
}: ConditionalFieldsProps<T>) {
  const conditionsArray = Array.isArray(conditions) ? conditions : [conditions];

  // Watch all condition fields
  const watchedValues = conditionsArray.reduce(
    (acc, condition) => {
      acc[condition.field] = watch(condition.field);
      return acc;
    },
    {} as Record<Path<T>, unknown>
  );

  // Evaluate conditions
  const evaluateCondition = (condition: Condition<T>): boolean => {
    const fieldValue = watchedValues[condition.field];

    switch (condition.operator) {
      case 'equals':
        return fieldValue === condition.value;
      case 'notEquals':
        return fieldValue !== condition.value;
      case 'contains':
        return (
          (typeof fieldValue === 'string' && fieldValue.includes(condition.value as string)) ||
          false
        );
      case 'greaterThan':
        return Number(fieldValue) > Number(condition.value);
      case 'lessThan':
        return Number(fieldValue) < Number(condition.value);
      case 'in':
        return Array.isArray(condition.value) && condition.value.includes(fieldValue);
      case 'notIn':
        return Array.isArray(condition.value) && !condition.value.includes(fieldValue);
      default:
        return false;
    }
  };

  const results = conditionsArray.map(evaluateCondition);
  const shouldShow = mode === 'all' ? results.every(Boolean) : results.some(Boolean);

  // Handle callbacks
  React.useEffect(() => {
    if (shouldShow) {
      onShow?.();
    } else {
      onHide?.();
    }
  }, [shouldShow, onShow, onHide]);

  // Animation variants
  const animationVariants = {
    fade: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    slide: {
      initial: { opacity: 0, y: -20 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -20 },
    },
    scale: {
      initial: { opacity: 0, scale: 0.8 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 0.8 },
    },
    none: {
      initial: {},
      animate: {},
      exit: {},
    },
  };

  const variants = animationVariants[animationType];

  if (animationType === 'none') {
    return shouldShow ? <>{children}</> : null;
  }

  return (
    <AnimatePresence mode="wait">
      {shouldShow && (
        <motion.div
          initial={variants.initial}
          animate={variants.animate}
          exit={variants.exit}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Helper component for nested conditions
interface NestedConditionsProps<T extends FieldValues> {
  watch: UseFormWatch<T>;
  children: (
    evaluate: (conditions: Condition<T> | Condition<T>[], mode?: 'all' | 'any') => boolean
  ) => React.ReactNode;
}

export function NestedConditions<T extends FieldValues>({
  watch,
  children,
}: NestedConditionsProps<T>) {
  const evaluate = React.useCallback(
    (conditions: Condition<T> | Condition<T>[], mode: 'all' | 'any' = 'all') => {
      const conditionsArray = Array.isArray(conditions) ? conditions : [conditions];

      const results = conditionsArray.map((condition) => {
        const fieldValue = watch(condition.field);

        switch (condition.operator) {
          case 'equals':
            return fieldValue === condition.value;
          case 'notEquals':
            return fieldValue !== condition.value;
          case 'contains':
            return fieldValue?.includes?.(condition.value) || false;
          case 'greaterThan':
            return Number(fieldValue) > Number(condition.value);
          case 'lessThan':
            return Number(fieldValue) < Number(condition.value);
          case 'in':
            return Array.isArray(condition.value) && condition.value.includes(fieldValue);
          case 'notIn':
            return Array.isArray(condition.value) && !condition.value.includes(fieldValue);
          default:
            return false;
        }
      });

      return mode === 'all' ? results.every(Boolean) : results.some(Boolean);
    },
    [watch]
  );

  return <>{children(evaluate)}</>;
}

// Pre-built condition helpers
export const conditionHelpers = {
  equals: <T extends FieldValues>(field: Path<T>, value: unknown): Condition<T> => ({
    field,
    operator: 'equals',
    value,
  }),

  notEquals: <T extends FieldValues>(field: Path<T>, value: unknown): Condition<T> => ({
    field,
    operator: 'notEquals',
    value,
  }),

  contains: <T extends FieldValues>(field: Path<T>, value: unknown): Condition<T> => ({
    field,
    operator: 'contains',
    value,
  }),

  greaterThan: <T extends FieldValues>(field: Path<T>, value: number): Condition<T> => ({
    field,
    operator: 'greaterThan',
    value,
  }),

  lessThan: <T extends FieldValues>(field: Path<T>, value: number): Condition<T> => ({
    field,
    operator: 'lessThan',
    value,
  }),

  in: <T extends FieldValues>(field: Path<T>, values: unknown[]): Condition<T> => ({
    field,
    operator: 'in',
    value: values,
  }),

  notIn: <T extends FieldValues>(field: Path<T>, values: unknown[]): Condition<T> => ({
    field,
    operator: 'notIn',
    value: values,
  }),
};
